import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Send, Star, Calendar } from './icons/CustomDashboardIcons';
import { MessageSquare } from './icons/MessageSquareIcon';

const SESSIONS = [
  { id: 'session-1', name: 'Session 1: Introduction to NVC', description: 'Foundation concepts and the OFNR model' },
  { id: 'session-2', name: 'Session 2: Observations vs. Evaluations', description: 'Learning to observe without judging' },
  { id: 'session-3', name: 'Session 3: Feelings', description: 'Identifying and expressing feelings' },
  { id: 'session-4', name: 'Session 4: Needs', description: 'Understanding universal human needs' },
  { id: 'session-5', name: 'Session 5: Requests', description: 'Making clear, doable requests' },
  { id: 'session-6', name: 'Session 6: Empathic Listening', description: 'Deep listening and presence' },
  { id: 'session-7', name: 'Session 7: Self-Empathy', description: 'Compassion for ourselves' },
  { id: 'session-8', name: 'Session 8: Integration & Practice', description: 'Bringing it all together' }
];

function FeedbackForm({ userId, userName }) {
  const [selectedSession, setSelectedSession] = useState(null);
  const [feedbackData, setFeedbackData] = useState({
    feedback_type: 'session',
    session_id: '',
    session_name: '',
    subject: '',
    message: '',
    suggestions: '',
    requests: '',
    ratings: {
      program_content: 0,
      presenter_knowledge: 0,
      presenter_delivery: 0,
      met_expectations: 0,
      overall_quality: 0
    },
    willing_testimonial: false,
    testimonial_text: '',
    testimonial_name: userName || ''
  });

  const [submitting, setSubmitting] = useState(false);

  const updateRating = (category, value) => {
    setFeedbackData(prev => ({
      ...prev,
      ratings: { ...prev.ratings, [category]: value }
    }));
  };

  const selectSession = (session) => {
    setSelectedSession(session);
    setFeedbackData(prev => ({
      ...prev,
      session_id: session.id,
      session_name: session.name
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSession) {
      toast.error('Please select a session to provide feedback for');
      return;
    }
    
    setSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          ...feedbackData,
          send_to_admin: true  // Flag to send email to admin team
        })
      });

      if (!response.ok) throw new Error('Submission failed');

      toast.success('Thank you! Your feedback has been submitted and sent to the team.');
      
      // Reset form
      setSelectedSession(null);
      setFeedbackData({
        feedback_type: 'session',
        session_id: '',
        session_name: '',
        subject: '',
        message: '',
        suggestions: '',
        requests: '',
        ratings: {
          program_content: 0,
          presenter_knowledge: 0,
          presenter_delivery: 0,
          met_expectations: 0,
          overall_quality: 0
        },
        willing_testimonial: false,
        testimonial_text: '',
        testimonial_name: userName || ''
      });
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const ratingCategories = [
    { key: 'program_content', label: 'Program Content' },
    { key: 'presenter_knowledge', label: 'Presenter(s) Knowledge' },
    { key: 'presenter_delivery', label: 'Presenter(s) Delivery' },
    { key: 'met_expectations', label: 'Program Met My Expectations' },
    { key: 'overall_quality', label: 'Overall Quality of the Program' }
  ];

  return (
    <div className="space-y-6">
      {/* Session Selection */}
      {!selectedSession ? (
        <div className="space-y-4">
          <div className="text-center mb-6">
            <h3 className="text-xl text-white font-light mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Select a Session
            </h3>
            <p className="text-stone-400 text-sm">Choose which session you'd like to provide feedback for</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {SESSIONS.map((session) => (
              <button
                key={session.id}
                onClick={() => selectSession(session)}
                className="group text-left bg-stone-900 border border-stone-700 rounded-lg p-4 hover:border-amber-500/50 hover:bg-stone-800/50 transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {session.id.split('-')[1]}
                  </div>
                  <div>
                    <h4 className="text-white font-medium group-hover:text-amber-400 transition-colors text-sm">
                      {session.name}
                    </h4>
                    <p className="text-stone-500 text-xs mt-1">{session.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Selected Session Header */}
          <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center font-bold">
                {selectedSession.id.split('-')[1]}
              </div>
              <div>
                <h4 className="text-white font-medium">{selectedSession.name}</h4>
                <p className="text-stone-400 text-xs">{selectedSession.description}</p>
              </div>
            </div>
            <Button 
              type="button" 
              variant="ghost" 
              onClick={() => setSelectedSession(null)}
              className="text-stone-400 hover:text-white"
            >
              Change Session
            </Button>
          </div>

          {/* Ratings */}
          <div className="space-y-4 bg-stone-900/50 border border-stone-800 rounded-lg p-5">
            <h4 className="text-white font-medium flex items-center gap-2 mb-4">
              <Star className="w-4 h-4 text-amber-500" />
              Rate This Session
            </h4>
            {ratingCategories.map((category) => (
              <div key={category.key} className="space-y-2">
                <Label className="text-stone-300 text-sm">{category.label}</Label>
                <RadioGroup 
                  value={feedbackData.ratings[category.key].toString()} 
                  onValueChange={(value) => updateRating(category.key, parseInt(value))}
                  className="flex gap-3"
                >
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <div key={rating} className="flex flex-col items-center">
                      <RadioGroupItem 
                        value={rating.toString()} 
                        id={`${category.key}-${rating}`}
                        className="border-stone-600 text-amber-500"
                      />
                      <Label htmlFor={`${category.key}-${rating}`} className="text-xs text-stone-500 mt-1">{rating}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            ))}
          </div>

          {/* Comments */}
          <div className="space-y-2">
            <Label className="text-white flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-amber-500" />
              Comments & Reflections
            </Label>
            <Textarea
              value={feedbackData.message}
              onChange={(e) => setFeedbackData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Share your thoughts about this session..."
              className="bg-stone-900 border-stone-700 text-white placeholder:text-stone-500 min-h-[100px]"
            />
          </div>

          {/* Suggestions */}
          <div className="space-y-2">
            <Label className="text-white">Suggestions for Improvement</Label>
            <Textarea
              value={feedbackData.suggestions}
              onChange={(e) => setFeedbackData(prev => ({ ...prev, suggestions: e.target.value }))}
              placeholder="What could we do better? How can we improve this session?"
              className="bg-stone-900 border-stone-700 text-white placeholder:text-stone-500 min-h-[80px]"
            />
          </div>

          {/* Requests */}
          <div className="space-y-2">
            <Label className="text-white">Requests or Topics You'd Like Covered</Label>
            <Textarea
              value={feedbackData.requests}
              onChange={(e) => setFeedbackData(prev => ({ ...prev, requests: e.target.value }))}
              placeholder="Is there something specific you'd like us to address or cover in future sessions?"
              className="bg-stone-900 border-stone-700 text-white placeholder:text-stone-500 min-h-[80px]"
            />
          </div>

          {/* Testimonial Section */}
          <div className="space-y-4 bg-amber-500/5 border border-amber-500/20 rounded-lg p-5">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="testimonial"
                checked={feedbackData.willing_testimonial}
                onCheckedChange={(checked) => setFeedbackData(prev => ({ ...prev, willing_testimonial: checked }))}
                className="border-amber-500/50 data-[state=checked]:bg-amber-500"
              />
              <Label htmlFor="testimonial" className="text-stone-300 font-normal cursor-pointer">
                I'm willing to share a testimonial for the website
              </Label>
            </div>

            {feedbackData.willing_testimonial && (
              <div className="space-y-4 pt-4 border-t border-amber-500/20">
                <div className="space-y-2">
                  <Label className="text-white">Your Testimonial</Label>
                  <Textarea
                    value={feedbackData.testimonial_text}
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, testimonial_text: e.target.value }))}
                    placeholder="Share your experience with The Belonging Sessions..."
                    className="bg-stone-900 border-stone-700 text-white placeholder:text-stone-500 min-h-[100px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Display Name (optional)</Label>
                  <Input
                    value={feedbackData.testimonial_name}
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, testimonial_name: e.target.value }))}
                    placeholder="How should we credit your testimonial?"
                    className="bg-stone-900 border-stone-700 text-white placeholder:text-stone-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Submit */}
          <Button 
            type="submit" 
            disabled={submitting}
            className="w-full bg-amber-600 hover:bg-amber-500 text-black font-semibold py-3 rounded-none"
          >
            <Send className="w-4 h-4 mr-2" />
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>

          <p className="text-stone-500 text-xs text-center">
            Your feedback will be sent to the Belonging Sessions team
          </p>
        </form>
      )}
    </div>
  );
}

export default FeedbackForm;